/**
 * BulkActionsBar.jsx — sticky floating bar shown when one or more rows are
 * selected. Lives above the mobile bottom nav and above the desktop viewport
 * footer. Drop in as many `actions` as the caller needs — each renders as a
 * small pill button.
 *
 * Extracted from Inventory.jsx (iter 102).
 *
 * Props:
 *   - count: number of selected items
 *   - onClear: callback to clear selection
 *   - actions: Array<{ key, label, icon?, onClick, testId?, title? }>
 *   - testId: optional override for the outer region testid
 */
import React from 'react';
import { X } from '@phosphor-icons/react';

export default function BulkActionsBar({ count, onClear, actions = [], testId = 'selection-action-bar' }) {
  if (!count) return null;
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40 bottom-20 md:bottom-6 bg-foreground text-background border border-border shadow-lg rounded-full px-3 py-2 flex items-center gap-2 max-w-[95vw]"
      data-testid={testId}
      role="region"
      aria-label="Bulk actions"
    >
      <span className="text-xs font-bold tracking-wider uppercase pl-2 pr-1" data-testid="selection-count">
        {count} selected
      </span>
      {actions.map((a) => (
        <button
          key={a.key}
          onClick={a.onClick}
          data-testid={a.testId}
          title={a.title}
          className="bg-background text-foreground hover:bg-muted text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-full flex items-center gap-1.5 transition"
        >
          {a.icon}
          {a.label}
        </button>
      ))}
      <button
        onClick={onClear}
        data-testid="clear-selection-btn"
        className="text-background/80 hover:text-background p-1.5 rounded-full hover:bg-background/10"
        aria-label="Clear selection"
        title="Clear selection"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
